import React, { useEffect, useState } from 'react';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import EmployeeAvatar from '@/components/employees/EmployeeAvatar';
import useTranslation from '@/hooks/useTranslation';
import formatTranslation from '@/utils/formatTranslation';

const RequestModal = ({
  request,
  isOpen,
  onClose,
  onApprove,
  onDeny
}) => {
  const { t, language } = useTranslation(['timeOffManagement', 'common']);
  const translate = (key, fallback) => t(`timeOffManagement:${key}`, fallback);
  const locale = language || 'en-US';
  const [comment, setComment] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setComment('');
      setErrorMessage('');
      setIsProcessing(false);
    } else {
      setErrorMessage('');
      setIsProcessing(false);
    }
  }, [isOpen, request?.id]);

  if (!isOpen || !request) return null;

  const handleApprove = async () => {
    setIsProcessing(true);
    setErrorMessage('');
    try {
      await onApprove(request?.id, comment);
      setComment('');
      onClose();
    } catch (error) {
      const message =
        error?.response?.data?.message
        ?? error?.message
        ?? translate('modal.request.errorApprove', 'Unable to approve this request. Please try again.');
      setErrorMessage(message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeny = async () => {
    setIsProcessing(true);
    setErrorMessage('');
    try {
      await onDeny(request?.id, comment);
      setComment('');
      onClose();
    } catch (error) {
      const message =
        error?.response?.data?.message
        ?? error?.message
        ?? translate('modal.request.errorDeny', 'Unable to deny this request. Please try again.');
      setErrorMessage(message);
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'vacation': return 'Palmtree';
      case 'sick': return 'Heart';
      case 'personal': return 'User';
      case 'emergency': return 'AlertTriangle';
      default: return 'Calendar';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-warning text-warning-foreground';
      case 'approved': return 'bg-success text-success-foreground';
      case 'denied': return 'bg-error text-error-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusLabel = (status) => {
    if (!status) {
      return translate('statuses.pending', 'Pending');
    }
    return translate(`statuses.${status}`, status.charAt(0).toUpperCase() + status.slice(1));
  };

  const getTypeLabel = (type) => {
    if (!type) {
      return translate('types.default', 'Absence');
    }
    return translate(`types.${type}`, type.charAt(0).toUpperCase() + type.slice(1));
  };

  const formatDurationLabel = (duration) => {
    const count = typeof duration === 'number' ? duration : 0;
    return formatTranslation(
      translate(
        `duration.days.${count === 1 ? 'singular' : 'plural'}`,
        count === 1 ? '{{count}} day' : '{{count}} days',
      ),
      { count },
    );
  };

  const employeeDisplayName = request?.employee?.name ?? translate('card.unknownName', 'Employee');
  const avatarAlt = formatTranslation(
    translate('card.avatarAlt', '{{name}} profile photo'),
    { name: employeeDisplayName },
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {translate('modal.request.title', 'Absence Request Details')}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Employee Info */}
          <div className="flex items-start space-x-4">
            <EmployeeAvatar
              url={request?.employee?.avatarUrl}
              name={request?.employee?.name}
              alt={avatarAlt}
              status={request?.employee?.status}
              size="lg"
              showStatus={Boolean(request?.employee?.status)}
              wrapperClassName="border border-border"
            />
            <div className="flex-1">
              <h3 className="text-lg font-medium text-foreground">{request?.employee?.name}</h3>
              <p className="text-muted-foreground">{request?.employee?.position}</p>
              <p className="text-sm text-muted-foreground">
                {request?.employee?.department} • {request?.employee?.email}
              </p>
              <div className="flex items-center mt-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request?.status)}`}>
                  {getStatusLabel(request?.status)}
                </span>
              </div>
            </div>
          </div>

          {/* Request Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {translate('modal.request.fields.type', 'Request Type')}
                </label>
                <div className="flex items-center mt-1">
                  <Icon name={getTypeIcon(request?.type)} size={16} className="mr-2 text-muted-foreground" />
                  <span className="text-foreground capitalize">{getTypeLabel(request?.type)}</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {translate('modal.request.fields.startDate', 'Start Date')}
                </label>
                <p className="text-foreground mt-1">{formatDate(request?.startDate)}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {translate('modal.request.fields.endDate', 'End Date')}
                </label>
                <p className="text-foreground mt-1">{formatDate(request?.endDate)}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {translate('modal.request.fields.duration', 'Duration')}
                </label>
                <p className="text-foreground mt-1">{formatDurationLabel(request?.duration)}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {translate('modal.request.fields.submitted', 'Submitted')}
                </label>
                <p className="text-foreground mt-1">{formatDate(request?.submittedDate)}</p>
              </div>

            </div>
          </div>

          {/* Reason */}
          {request?.reason && (<div>
            <label className="text-sm font-medium text-muted-foreground">
              {translate('employeeView.requests.reasonLabel', 'Reason')}
            </label>
            <p className="text-foreground mt-1 p-3 bg-muted/30 rounded-lg">{request?.reason}</p>
          </div>)}
        </div>

        {/* Actions */}
        {request?.status === 'pending' && (
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isProcessing}
            >
              {translate('actions.cancel', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeny}
              loading={isProcessing}
            >
              <Icon name="X" size={16} className="mr-2" />
              {translate('modal.request.actions.deny', 'Deny Request')}
            </Button>
            <Button
              variant="success"
              onClick={handleApprove}
              loading={isProcessing}
            >
              <Icon name="Check" size={16} className="mr-2" />
              {translate('modal.request.actions.approve', 'Approve Request')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestModal;
